/** Every column the table can show, in the order it shows them. */
export const AUDIT_COLUMN_IDS = [
    'time',
    'actor',
    'action',
    'target',
    'outcome',
    'source',
    'request',
    'requestId',
    'userAgent',
    'entryId',
] as const;

export type AuditColumnId = (typeof AUDIT_COLUMN_IDS)[number];

/** Time opens the details and Action carries the summary, so neither can be hidden. */
export const HIDEABLE_AUDIT_COLUMNS = [
    'actor',
    'target',
    'outcome',
    'source',
    'request',
    'requestId',
    'userAgent',
    'entryId',
] as const satisfies readonly AuditColumnId[];

export type AuditHideableColumnId = (typeof HIDEABLE_AUDIT_COLUMNS)[number];

export type AuditColumnVisibility = Record<AuditHideableColumnId, boolean>;

export const DEFAULT_AUDIT_COLUMN_VISIBILITY: Readonly<AuditColumnVisibility> = Object.freeze({
    actor: true,
    target: true,
    outcome: true,
    source: true,
    request: false,
    requestId: false,
    userAgent: false,
    entryId: false,
});

interface AuditColumnSpec {
    headerKey: string;
    /** A CSS grid track; the widths were measured at 1280, 1440 and 1920 wide. */
    track: string;
}

export const AUDIT_COLUMNS: Readonly<Record<AuditColumnId, AuditColumnSpec>> = {
    time: { headerKey: 'auditLog.table.time', track: '92px' },
    actor: { headerKey: 'auditLog.table.actor', track: 'minmax(150px, 1fr)' },
    action: { headerKey: 'auditLog.table.action', track: 'minmax(220px, 1.8fr)' },
    target: { headerKey: 'auditLog.table.target', track: 'minmax(180px, 1.2fr)' },
    outcome: { headerKey: 'auditLog.table.outcome', track: '112px' },
    source: { headerKey: 'auditLog.table.source', track: '150px' },
    request: { headerKey: 'auditLog.table.request', track: 'minmax(170px, 1fr)' },
    requestId: { headerKey: 'auditLog.table.requestId', track: '112px' },
    userAgent: { headerKey: 'auditLog.table.userAgent', track: 'minmax(170px, 1fr)' },
    entryId: { headerKey: 'auditLog.table.entryId', track: '88px' },
};

export function isHideableAuditColumn(id: string): id is AuditHideableColumnId {
    return HIDEABLE_AUDIT_COLUMNS.some((hideable) => hideable === id);
}

/** The columns to render, in table order; the ones that cannot be hidden always are. */
export function visibleAuditColumns(visibility: AuditColumnVisibility): AuditColumnId[] {
    return AUDIT_COLUMN_IDS.filter((id) => !isHideableAuditColumn(id) || visibility[id]);
}
